import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Close from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme
} from '@mui/material';
import { SearchContext } from 'context/SearchContext';
import { useBreakpoints } from 'hooks/useBreakpoints';
import React, { useContext, useState } from 'react';
import { tss } from 'tss-react/mui';
import { ProductType } from 'types/Products';
import { ClearFilterSection } from './ClearFilterSection';
import { DropDownFilters } from './DropDownFilters';
import { getUxbFilterOptions } from './uxbeautyFilterOptions';

interface FilterMenuProps {
  anchorEl: HTMLElement | null;
  setAnchorEl: (anchorEl: HTMLElement | null) => void;
  productType: ProductType;
}

export const FilterMenu: React.FC<FilterMenuProps> = props => {
  const [selectedFilterOption, setSelectedFilterOption] = useState<string>();
  const { width } = useBreakpoints();
  const isMobile = width < useTheme().breakpoints.values.sm;

  const isTopLevel = isMobile && selectedFilterOption === undefined;
  const handleCloseMenu = () => {
    props.setAnchorEl(null);
    setSelectedFilterOption(undefined);
  };
  const { classes } = useStyles();
  const { request: searchRequest } = useContext(SearchContext);

  const filterOptions = getUxbFilterOptions(props.productType);

  return isMobile ? (
    <Drawer
      open={Boolean(props.anchorEl)}
      onClose={handleCloseMenu}
      classes={{ paper: classes.paper }}
    >
      <Box
        key="title"
        sx={theme => ({
          display: 'flex',
          justifyContent: 'space-between',
          padding: `6px ${theme.spacing(1)} 6px ${theme.spacing(2)}`
        })}
      >
        <Typography variant="h3">Filters</Typography>
        <IconButton onClick={handleCloseMenu}>
          <Close />
        </IconButton>
      </Box>
      {isTopLevel ? (
        <>
          {filterOptions.map(o => (
            <MenuItem
              key={o.key}
              onClick={() => setSelectedFilterOption(o.key)}
              sx={{
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              {o.label}
              <ArrowForwardIosIcon color="primary" sx={{ ml: 1 }} />
            </MenuItem>
          ))}
          {searchRequest.filters?.length > 0 && (
            <>
              <Divider />
              <Typography
                sx={theme => ({
                  padding: `6px ${theme.spacing(1)} 6px ${theme.spacing(2)}`
                })}
              >
                Filters Selected:
              </Typography>
              <Box
                sx={theme => ({
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: `6px ${theme.spacing(1)} 6px 12px`
                })}
              >
                <ClearFilterSection />
              </Box>
            </>
          )}
        </>
      ) : (
        // TODO make this generic later...

        <DropDownFilters
          setSelectedFilterOption={setSelectedFilterOption}
          selectedFilterOption={selectedFilterOption}
          options={filterOptions}
        />
      )}
    </Drawer>
  ) : (
    <Box
      sx={{
        display: {
          xs: 'none',
          sm: 'flex',
          flexWrap: 'wrap',
          gap: 15
        }
      }}
    >
      {filterOptions.map(el => (
        <React.Fragment key={el.key}>
          <Button
            variant="outlined"
            className={selectedFilterOption === el.key ? 'active' : ''}
            sx={{ whiteSpace: 'nowrap' }}
            endIcon={<ExpandMoreIcon color="primary" />}
            onClick={event => {
              event.stopPropagation();
              props.setAnchorEl(event.target as HTMLElement);
              setSelectedFilterOption(el.key);
            }}
          >
            {el.label}
          </Button>
        </React.Fragment>
      ))}
      {props.anchorEl && selectedFilterOption && (
        <Menu
          open={Boolean(props.anchorEl)}
          onClose={handleCloseMenu}
          anchorEl={props.anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          MenuListProps={{
            sx: {
              maxHeight: 270,
              overflowY: 'auto',
              pt: 0
            }
          }}
        >
          <DropDownFilters
            setSelectedFilterOption={setSelectedFilterOption}
            selectedFilterOption={selectedFilterOption}
            options={filterOptions}
          />
        </Menu>
      )}
    </Box>
  );
};
const useStyles = tss.create(() => ({
  paper: {
    width: '70%'
  }
}));
